import React, { useEffect, useRef } from 'react';

import { DocumentMode } from '../models/Types';
import { FULLSTORY_EXCLUDE } from '../constants/analytics';

export const IFRAME_ID = 'zdocsIframe';

export enum IFRAME_EVENTS {
  ReviewComplete = 'reviewComplete',
  DocumentSaved = 'documentSaved',
  DocumentSavedSummary = 'documentSavedSummary',
  DocumentRevisionRefresh = 'documentRevisionRefresh',
  FieldClicked = 'fieldClicked',
  FieldPlaced = 'fieldPlaced',
  FieldRemoved = 'fieldRemoved',
  ModalOpened = 'modalOpened',
  ModalClosed = 'modalClosed',
  Error = 'error',
}

export enum IFRAME_ERROR_TYPES {
  MissingToken = 'missingToken',
  InvalidToken = 'invalidToken',
  ExpiredToken = 'expiredToken',
  FetchError = 'fetchError',
  SaveError = 'saveError',
  PageImageError = 'pageImageError',
  Unknown = 'unknownError',
}

export enum IFRAME_COMMANDS {
  SAVE_DOCUMENT = 'saveDocument',
}

type Props = {
  leaseId: string;
  documentUrl: string;
  documentMode: DocumentMode;
  saveLeaseEditorRequested?: boolean;
  cancelSaveLeaseEditor?(leaseId: string): void;
  onLoad(): void;
  onError(): void;
  onFieldPlaced?(): void;
  onFieldClicked?(): void;
  onFieldRemoved?(): void;
  onReviewComplete?(): void;
  onEditorSaved(): void;
  onEditorSaveError(): void;
  onSigningComplete(): void;
  onSigningError(): void;
  onExpiredToken?(): void;
};

export function ZDocsDocumentView(props: Props) {
  const iframeRef = useRef(null);

  function messageListener(msg) {
    const event: IFRAME_EVENTS = msg.data.event;
    const errorType: IFRAME_ERROR_TYPES = msg.data.meta?.error;

    if (event === IFRAME_EVENTS.Error) {
      // eslint-disable-next-line no-console
      errorType && console.info('Received error:', errorType);
    } else {
      // eslint-disable-next-line no-console
      event && console.info('Received event:', event);
    }

    const callbackMapping = {
      [IFRAME_EVENTS.FieldPlaced]: props.onFieldPlaced,
      [IFRAME_EVENTS.FieldClicked]: props.onFieldClicked,
      [IFRAME_EVENTS.FieldRemoved]: props.onFieldRemoved,
      [IFRAME_EVENTS.ReviewComplete]: props.onReviewComplete,
      [IFRAME_EVENTS.DocumentSavedSummary]: () => onDocumentSavedSummary(msg.data.meta.summary),
      [IFRAME_ERROR_TYPES.ExpiredToken]: props.onExpiredToken,
    };

    const callbackFunction = callbackMapping[event !== IFRAME_EVENTS.Error ? event : errorType];
    callbackFunction?.();
  }

  function onDocumentSavedSummary(documentSaveSummary: { [docId: string]: boolean }) {
    const success = !Object.values(documentSaveSummary).some((val) => !val);
    switch (props.documentMode) {
      case DocumentMode.FILL:
        success ? props.onSigningComplete() : props.onSigningError();
        return;
      case DocumentMode.EDIT:
        success ? props.onEditorSaved() : props.onEditorSaveError();
        return;
    }
  }

  function onSaveEditorRequested() {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ event: IFRAME_COMMANDS.SAVE_DOCUMENT }, '*');
      props.cancelSaveLeaseEditor?.(props.leaseId);
    } else if (iframeRef.current) {
      // contentWindow may be null in test environments (JSDOM)
      props.cancelSaveLeaseEditor?.(props.leaseId);
    }
  }

  function subscribeToMessages() {
    if (window) {
      window.addEventListener('message', messageListener);
    }
  }

  function unsubscribeFromMessages() {
    if (window) {
      window.removeEventListener('message', messageListener);
    }
  }

  useEffect(() => {
    subscribeToMessages();

    return () => {
      unsubscribeFromMessages();
    };
  }, [props.documentMode]);

  useEffect(() => {
    if (props.saveLeaseEditorRequested) {
      onSaveEditorRequested();
    }
  }, [props.saveLeaseEditorRequested]);

  return props.documentUrl ? (
    <iframe
      className={FULLSTORY_EXCLUDE}
      src={props.documentUrl}
      ref={iframeRef}
      onLoad={props.onLoad}
      onError={props.onError}
      id={IFRAME_ID}
      title="ZDocs Document"
      style={{ height: '100%', width: '100%', border: 'none', display: 'block' }}
    />
  ) : null;
}

export default ZDocsDocumentView;
